import {Component} from 'react';
import h from 'react-hyperscript';
import './ProgramAdvancementDetail.scss';
import BasicCard from 'Components/Cards/BasicCard';
import {
  h4,
  subhead,
  footnote,
  eyebrow} from 'Components/Typography/Typography';
import ProgressBar from 'Components/DataViz/ProgressBar/ProgressBar';
import TooltipGroup from 'Components/TooltipGroup/TooltipGroup';
import LoadingIndicator from 'Components/LoadingIndicator/LoadingIndicator';
import {RESOURCE} from 'Src/utils/gtm/const';
import {pushEvent as pushGTMEvent} from 'Src/utils/gtm';
import {
  setZeroIfInvalidNumber,
  isArrayEmpty,
  getPercentageDifference,
  getPercentValue} from 'Src/utils/utils';
import ReportError from 'Components/ErrorMessage/ReportError';
import Benchmarking from 'Components/Benchmarking/Benchmarking';

const dataKeys = {
  'num_l1s_district_detail': 'num_l1s_district',
  'num_l2s_district_detail': 'num_l2s_district',
};

class ProgramAdvancementDetail extends Component {
  constructor(props) {
    super(props);
    this._isMounted = false;
    this.state = {
      num_l1s_district: 0,
      num_l2s_district: 0,
      num_innovators_district: 0,
      num_trainers_district: 0,
      isLoaded: false,
      hasError: false,
    };
  }

  componentWillMount() {
    this._isMounted = true;
    this.setData();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  setData() {
    if (this.dataAvailable) {
      this.createGraphs();
    } else {
      this.setHasError(true);
    }
  }

  get dataAvailable() {
    return !isArrayEmpty(this.props.data) &&
    this.props.graphs &&
    typeof this.props.resources === 'object' &&
    !isArrayEmpty(this.props.resources);
  }

  setHasError(value) {
    this.setState({
      isLoaded: true,
      hasError: value,
    });
  }

  createGraphs() {
    this.props.graphs.forEach((graph, i) => {
      const id = graph.id;
      const dataId = dataKeys[id] || id;

      const value = setZeroIfInvalidNumber(this.props.data[dataId]);
      this.setGraph(dataId, value);

      if (this.props.dataPrev) {
        const valuePrev = setZeroIfInvalidNumber(this.props.dataPrev[dataId]);
        this.setGraph(dataId + 'Prev', valuePrev);
      }
    });
  }

  setGraph(id, value) {
    if (this._isMounted) {
      this.setState({
        [id]: value,
      }, () => {
        this.setState({
          isLoaded: true,
        });
      });
    }
  }

  createGraphHeader(graph, classes, key) {
    if (graph.tooltip) {
      return (
        h(TooltipGroup, {
          title: graph.tooltip,
          className: classes,
        }, h(subhead, {
          key: key,
        }, graph.title))
      );
    } else {
      return (
        h(subhead, {
          key: key,
          className: classes,
        }, graph.title)
      );
    }
  }

  render() {
    if (this.state.isLoaded) {
      return (
        h(BasicCard, {
          id: this.props.id,
          className: this.props.className,
        }, [
          h(h4, {
            key: 'title__key',
            className: 'card__title',
          }, this.props.title),

          this.state.hasError ? h('div', {
            className: 'padding-bottom--md',
          }, h(ReportError, {
            message: this.props.noDataError,
          }, null)) : h('div', null, [
            h('div', {
              key: 'graphs__key',
              className: 'flex flex--wrap flex--align-start',
            }, this.props.graphs.map((graph, i) => {
              const id = graph.id;
              const dataId = dataKeys[id] || id;
              const value = this.state[dataId];
              const valuePrev = this.state[dataId + 'Prev'];
              if (graph.type === 'progress') {
                const teachers = this.props.districtMetrics[`num_teachers`];
                const percentage = setZeroIfInvalidNumber(value/teachers);

                const progressComponents = [
                  h(ProgressBar, {
                    key: `${id}__key`,
                    id: `${id}`,
                    value: percentage,
                    height: 8,
                    width: 150,
                    color: graph.color,
                  }, null),
                  h(h4, {
                    className: 'padding-left--md-2',
                  }, `${getPercentValue(percentage)}%`),
                ];

                if (!isNaN(valuePrev)) {
                  const teachersPrev =
                      this.props.districtMetricsPrev[`num_teachers`];
                  const percentagePrev =
                      setZeroIfInvalidNumber(valuePrev/teachersPrev);
                  const change =
                      getPercentageDifference(percentage, percentagePrev);

                  progressComponents.push(
                      h(Benchmarking, {
                        change,
                        className:
                          'padding-left--micro padding-bottom--micro-2',
                      }
                      )
                  );
                }

                return (
                  h('div', {
                    key: `${id}-graph__key`,
                    className: 'program-advancement__metric',
                  }, [
                    h('div', {
                      className: 'padding-bottom--micro',
                    }, [
                      this.createGraphHeader(
                          graph,
                          null,
                          'program-advancement-eyebrow'
                      ),
                      h(footnote, {
                        key: `program-advancement-footnote`,
                        className: 'text--grey',
                      }, `${value} Certified`),
                    ]),
                    h('div', {
                      className:
                          'flex--space-between--all progress-bar__container',
                    }, progressComponents),
                  ])
                );
              } else {
                const progressComponents = [h(h4, null, value)];

                if (!isNaN(valuePrev)) {
                  const change = value - valuePrev;

                  progressComponents.push(
                      h(Benchmarking, {
                        change,
                        className:
                          'padding-left--micro padding-bottom--micro-2',
                      })
                  );
                }

                return (
                  h('div', {
                    key: `${id}-graph-container__key`,
                    className: 'program-advancement__metric',
                  }, [
                    this.createGraphHeader(
                        graph,
                        'program-advancement__title',
                        'program-advancement__title'
                    ),
                    h('div', {
                      className: 'flex program-advancement__metric__value',
                    }, progressComponents),
                  ])
                );
              }
            }),
            ),
            h('div', {
              id: 'program-advancement-resources',
            }, [
              h(eyebrow, {
                className: 'padding-top--md padding-bottom--md-2',
              }, this.props.resources.title),

              h('div', {
                className:
                  'flex flex--wrap-sm flex--space-between flex--align-start',
              }, [
                h('ul', {
                  className: 'list--blank col-ratio-1--sm',
                }, this.props.resources.levels.map((resource, i) => {
                  const href = resource.href;
                  return (
                    h('li', {
                      key: `resource-${i}-content`,
                      className: 'margin-bottom--sm',
                    }, h('a', {
                      className:
                        'cta text--blue text--small-2 padding-right--xl',
                      href,
                      target: '_blank',
                      onClick: () => {
                        pushGTMEvent(RESOURCE.TAG, {
                          event: RESOURCE.EVENT,
                          action: {
                            resource_action: RESOURCE.ACTION.RESOURCE_CLICK,
                          },
                          variables: {
                            resource_category: 'Education Certifications',
                            resource_url: href,
                          },
                        });
                      },
                    }, resource.title))
                  );
                })),
                h('ul', {
                  className: 'list--blank col-ratio-1--sm',
                }, this.props.resources.certifications.map((resource, i) => {
                  const href = resource.href;
                  return (
                    h('li', {
                      key: `resource-${i}-content`,
                      className: 'margin-bottom--sm',
                    }, h('a', {
                      className:
                        'cta text--blue text--small-2 padding-right--xl',
                      href,
                      target: '_blank',
                      onClick: () => {
                        pushGTMEvent(RESOURCE.TAG, {
                          event: RESOURCE.EVENT,
                          action: {
                            resource_action: RESOURCE.ACTION.RESOURCE_CLICK,
                          },
                          variables: {
                            resource_category: 'Education Certifications',
                            resource_url: href,
                          },
                        });
                      },
                    }, resource.title))
                  );
                })),
              ]),
            ]),
          ]),
        ])
      );
    } else {
      return (
        h(LoadingIndicator, null, null)
      );
    }
  }
}

export default ProgramAdvancementDetail;
